import { useMutation } from '@platform/api-client';
import { Input, MAX_DECIMAL_LENGTH, SettingsGroup, Switch } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { MAX_OPTIONS, variantLabel, type CatalogOption, type OptionIndex } from '../options.ts';
import { /* MAX_CODE_LENGTH, */ type ProductFormValues } from '../product-form.ts';
// import { autoSku } from '../product-variants.ts';
import { usePageContext } from '../../usePageContext.ts';
import { InlineCreate } from './InlineCreate.tsx';
import { ordinaryOptionsOf, originOptionOf } from '../origin.ts';
import type { OptionAxis } from '../product-variants.ts';
import { chosenValuesOf, withValueToggled } from './option-axes.ts';
import { OptionPicker } from './OptionPicker.tsx';
import { EditorSectionCard } from './EditorSectionCard.tsx';
import styles from './ProductEditor.module.css';
import { useFieldError, usePriceError, variantField } from './sections.ts';
import { useOptionAxes } from './useOptionAxes.ts';

export type VariantsSectionProps = {
  api: TypedClient;
  currency: string;
  options: readonly CatalogOption[];
  index: OptionIndex;
  canSeeCost: boolean;
};

const byPosition = (left: CatalogOption, right: CatalogOption) => left.position - right.position;

export const VariantsSection = ({ api, currency, options, index, canSeeCost }: VariantsSectionProps) => {
  const { t, describeError, invalidate } = usePageContext();
  const { control, register, formState } = useFormContext<ProductFormValues>();
  const errorText = useFieldError();
  const priceError = usePriceError();
  const { axes, apply } = useOptionAxes(index);
  const originId = originOptionOf(options)?.id;
  const isOrigin = (axis: OptionAxis) => axis.optionId === originId;
  const ordinaryAxes = axes.filter((axis) => !isOrigin(axis));
  const [showsOptions, setShowsOptions] = useState(ordinaryAxes.length > 0);
  const [newOption, setNewOption] = useState('');
  const [rows, perVariantPrice /* , reference */, , defaultLocale] = useWatch<
    ProductFormValues,
    ['variants', 'perVariantPrice', 'reference', 'defaultLocale']
  >({ control, name: ['variants', 'perVariantPrice', 'reference', 'defaultLocale'] });
  const hasOptions = axes.length > 0;
  const sold = rows.filter((row) => row.enabled).length;
  const listError = errorText(formState.errors.variants?.root ?? formState.errors.variants);
  const atLimit = ordinaryAxes.length >= MAX_OPTIONS;

  const createOption = useMutation({
    mutationFn: (name: string) =>
      api.post('/admin/options', { body: { names: [{ locale: defaultLocale, name }] } }),
    onSuccess: async () => {
      setNewOption('');
      await invalidate('options');
    },
  });

  return (
    <EditorSectionCard section="variants">
      <div className={styles['stack']}>
        <SettingsGroup>
          <Switch
            label={t('productEditor.hasOptions')}
            helperText={t('productEditor.hasOptionsHelp')}
            checked={showsOptions}
            onChange={(event) => {
              setShowsOptions(event.target.checked);
              if (!event.target.checked) apply(axes.filter(isOrigin));
            }}
          />
        </SettingsGroup>
        {showsOptions ? (
          <div className={styles['pickers']}>
            <p className={styles['help']}>{t('productEditor.optionsHelp')}</p>
            {ordinaryOptionsOf(options)
              .toSorted(byPosition)
              .map((option) => {
                const chosen = chosenValuesOf(axes, option.id);
                return (
                  <OptionPicker
                    key={option.id}
                    api={api}
                    option={option}
                    chosen={chosen}
                    locked={atLimit && chosen.length === 0}
                    nameLocale={defaultLocale}
                    onToggle={(valueId, freshCodes) => {
                      apply(withValueToggled(options, axes, option, valueId), freshCodes);
                    }}
                  />
                );
              })}
            <InlineCreate
              label={t('productEditor.newOptionName')}
              actionLabel={t('productEditor.createOption')}
              value={newOption}
              pending={createOption.isPending}
              onChange={setNewOption}
              onCreate={(name) => {
                createOption.mutate(name);
              }}
            />
            {createOption.error === null ? null : (
              <p role="alert" className={styles['fieldError']}>
                {describeError(createOption.error)}
              </p>
            )}
          </div>
        ) : null}
        <p className={styles['help']} role="status">
          {t('productEditor.variantsOnSale', { count: sold })}
        </p>
        {listError === undefined ? null : (
          <p role="alert" className={styles['fieldError']}>
            {listError}
          </p>
        )}
        <ol className={styles['variantList']}>
          {rows.map((row, position) => {
            const errors = formState.errors.variants?.[position];
            const label = variantLabel(row.valueIds, index, t('productEditor.singleVariant'));
            return (
              <li
                key={row.valueIds.join('|') || 'single'}
                className={[styles['variantRow'], row.enabled ? '' : styles['variantOff']].join(' ')}
              >
                {hasOptions ? (
                  <Switch label={label} {...register(variantField(position, 'enabled'))} />
                ) : (
                  <strong className={styles['variantName']}>{label}</strong>
                )}
                {/* <Input
                  label={t('catalog.sku')}
                  dir="ltr"
                  maxLength={MAX_CODE_LENGTH}
                  placeholder={autoSku(reference, row.valueCodes)}
                  helperText={t('productEditor.skuHelp')}
                  readOnly={!row.enabled}
                  errorText={errorText(errors?.sku)}
                  {...register(variantField(position, 'sku'))}
                /> */}
                {/* <Input
                  label={t('productEditor.barcode')}
                  dir="ltr"
                  maxLength={MAX_CODE_LENGTH}
                  readOnly={!row.enabled}
                  {...register(variantField(position, 'barcode'))}
                /> */}
                {perVariantPrice && canSeeCost ? (
                  <Input
                    label={t('productEditor.cost')}
                    suffix={currency}
                    inputMode="decimal"
                    dir="ltr"
                    maxLength={MAX_DECIMAL_LENGTH}
                    readOnly={!row.enabled}
                    errorText={errorText(errors?.cost)}
                    {...register(variantField(position, 'cost'))}
                  />
                ) : null}
                {perVariantPrice ? (
                  <Input
                    label={t('productEditor.sellingPrice')}
                    suffix={currency}
                    required
                    inputMode="decimal"
                    dir="ltr"
                    maxLength={MAX_DECIMAL_LENGTH}
                    readOnly={!row.enabled}
                    errorText={row.enabled ? priceError(errors?.price, row) : errorText(errors?.price)}
                    {...register(variantField(position, 'price'))}
                  />
                ) : null}
              </li>
            );
          })}
        </ol>
      </div>
    </EditorSectionCard>
  );
};
